import { useState } from "react"
import { BellRing, HandPlatter, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import {
  deleteInline,
  postInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
} from "@/lib/inline-update"

type WaiterCallButtonProps = {
  /** Server truth: the table has a call staff has not answered yet. */
  pending?: boolean
  /**
   * The shell renders this control twice — tab-bar center slot on mobile, nav
   * row on desktop — and both are in the DOM at once, hidden by CSS. Distinct
   * ids keep a Playwright locator unambiguous.
   */
  testId: string
  className?: string
}

/** Which confirmation is open; `null` means the diner has not asked for anything. */
type Intent = "call" | "cancel" | null

/**
 * The one "requires attention" trigger a diner fires on purpose — the rest
 * (cold food, a stuck payment, a table nobody cleared) the staff workspace
 * infers from data.
 *
 * Two heights like every other interactive control (44px mobile, 36px desktop).
 * The same button rings and retracts: pending is a live state the diner can
 * take back, not a dead end, so it stays focusable and keeps its own label.
 *
 * Both directions confirm first. A tap here puts a person in motion across the
 * floor, and the tab bar sits under the thumb on every screen — an accidental
 * brush should not send someone walking, and neither should it call them off.
 */
export function WaiterCallButton({ pending = false, testId, className }: WaiterCallButtonProps) {
  const [intent, setIntent] = useState<Intent>(null)
  // Which request is in flight, so the button can say whether it is ringing or
  // calling off rather than a generic "working".
  const [busy, setBusy] = useState<Intent>(null)

  const submit = (action: Exclude<Intent, null>) => {
    const request = action === "call" ? postInline("/dinein/waiter_calls") : deleteInline("/dinein/waiter_calls")

    setBusy(action)
    setIntent(null)
    request
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error) => toastInlineUpdateError(error))
      .finally(() => setBusy(null))
  }

  const label = busy
    ? busy === "call"
      ? t("ui.user.waiter_call.calling")
      : t("ui.user.waiter_call.cancelling")
    : pending
      ? t("ui.user.waiter_call.pending_action")
      : t("ui.user.waiter_call.call")

  return (
    <>
      <button
        type="button"
        onClick={() => setIntent(pending ? "cancel" : "call")}
        disabled={busy !== null}
        aria-label={label}
        data-testid={testId}
        data-state={busy ? "busy" : pending ? "pending" : "idle"}
        className={cn(
          "flex size-11 shrink-0 items-center justify-center rounded-full transition-colors md:size-9",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70",
          pending
            ? "bg-brand-subtle text-brand hover:bg-brand-subtle/80"
            : "bg-primary text-primary-foreground hover:bg-primary/90",
          className
        )}
      >
        {busy ? (
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        ) : pending ? (
          // The pulse is the whole point of the pending state: a waiter is
          // already walking, so the control reads as in-motion rather than idle.
          <HandPlatter className="size-5 animate-pulse motion-reduce:animate-none" aria-hidden="true" />
        ) : (
          <BellRing className="size-5" aria-hidden="true" />
        )}
      </button>

      {/* A live region announces changes to its CONTENT, so the state has to be
          text in the DOM — putting aria-live on the button and changing only
          its aria-label announces nothing in most screen readers. The
          confirmation drawer steals focus before the request lands, so without
          this the diner is never told the waiter is coming.
          Empty while idle: the button already carries that name, and repeating
          it here would read the same phrase twice in a row. */}
      <span role="status" className="sr-only">
        {pending || busy ? label : ""}
      </span>

      <WaiterCallConfirmDrawer
        open={intent === "call"}
        onOpenChange={(open) => !open && setIntent(null)}
        testId="waiter-call-confirm"
        title={t("ui.user.waiter_call.confirm_title")}
        body={t("ui.user.waiter_call.confirm_body")}
        confirmLabel={t("ui.user.waiter_call.confirm_action")}
        onConfirm={() => submit("call")}
      />

      <WaiterCallConfirmDrawer
        open={intent === "cancel"}
        onOpenChange={(open) => !open && setIntent(null)}
        testId="waiter-cancel-confirm"
        title={t("ui.user.waiter_call.cancel_title")}
        body={t("ui.user.waiter_call.cancel_body")}
        confirmLabel={t("ui.user.waiter_call.cancel_action")}
        confirmVariant="destructive"
        onConfirm={() => submit("cancel")}
      />
    </>
  )
}

type WaiterCallConfirmDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  testId: string
  title: string
  body: string
  confirmLabel: string
  confirmVariant?: "default" | "destructive"
  onConfirm: () => void
}

/**
 * Bottom sheet on every breakpoint, not `ResponsiveDrawer`: a two-line
 * confirmation sliding in from the side of a desktop window reads as a panel
 * the diner has to deal with, rather than a question they can wave away.
 */
function WaiterCallConfirmDrawer({
  open,
  onOpenChange,
  testId,
  title,
  body,
  confirmLabel,
  confirmVariant = "default",
  onConfirm,
}: WaiterCallConfirmDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
      <DrawerContent className="mx-auto w-full max-w-lg" data-testid={testId}>
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-lg tracking-tight">{title}</DrawerTitle>
          <DrawerDescription>{body}</DrawerDescription>
        </DrawerHeader>
        <DrawerFooter className="pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <Button
            type="button"
            size="lg"
            variant={confirmVariant}
            onClick={onConfirm}
            data-testid={`${testId}-accept`}
          >
            {confirmLabel}
          </Button>
          <DrawerClose asChild>
            <Button type="button" size="lg" variant="ghost" data-testid={`${testId}-dismiss`}>
              {t("ui.user.waiter_call.dismiss")}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
